import { useEffect, useMemo, useState } from 'react'
import BarChart from '../components/BarChart'
import LineChart from '../components/LineChart'
import SegmentedControl from '../components/SegmentedControl'
import ToggleChips from '../components/ToggleChips'
import {
  AGE_GROUPS,
  DEVICES,
  DIMENSIONS,
  GENDERS,
  aggregateStatistics,
  applyFilters,
  dimensionLabel,
  valueLabel,
} from '../lib/statistics'
import { loadStatistics, loadStatisticsMetadata } from '../lib/statistics-api'
import type {
  Dimension,
  Separator,
  StatisticFilters,
  StatisticsPayload,
  TableRow,
} from '../types'

const KEYWORD_STORAGE_KEY = 'dsm-web-keywords'

const emptyFilters: StatisticFilters = {
  startMonth: '',
  endMonth: '',
  keywords: [],
  ageGroups: [...AGE_GROUPS],
  devices: [...DEVICES],
  genders: [...GENDERS],
}

function parseKeywords(value: string) {
  const seen = new Set<string>()
  const result: string[] = []

  for (const line of value.split(/\r?\n/)) {
    const keyword = line.trim()
    const normalized = keyword.toLocaleLowerCase('ko-KR')
    if (!keyword || seen.has(normalized)) continue
    seen.add(normalized)
    result.push(keyword)
    if (result.length === 100) break
  }

  return result
}

function loadSavedKeywords() {
  try {
    const value = JSON.parse(localStorage.getItem(KEYWORD_STORAGE_KEY) ?? '[]')
    return Array.isArray(value) ? value.filter((item): item is string => typeof item === 'string') : []
  } catch {
    return []
  }
}

function escapeCsv(value: string | number) {
  const text = String(value)
  return /[",\r\n]/.test(text) ? `"${text.replaceAll('"', '""')}"` : text
}

export default function KeywordToolPage() {
  const [payload, setPayload] = useState<StatisticsPayload | null>(null)
  const [keywords, setKeywords] = useState<string[]>([])
  const [loadError, setLoadError] = useState('')
  const [querying, setQuerying] = useState(false)
  const [draftFilters, setDraftFilters] = useState<StatisticFilters>(emptyFilters)
  const [appliedFilters, setAppliedFilters] = useState<StatisticFilters>(emptyFilters)
  const [keywordInput, setKeywordInput] = useState('')
  const [savedKeywords, setSavedKeywords] = useState<string[]>([])
  const [managerText, setManagerText] = useState('')
  const [managerOpen, setManagerOpen] = useState(false)
  const [missingKeywords, setMissingKeywords] = useState<string[]>([])
  const [xAxis, setXAxis] = useState<Dimension>('month')
  const [seriesDimension, setSeriesDimension] = useState<Dimension>('device')
  const [separator, setSeparator] = useState<Separator>('none')
  const [columns, setColumns] = useState(2)
  const [equalYAxis, setEqualYAxis] = useState(true)
  const [validationMessage, setValidationMessage] = useState('')
  const [tableSearch, setTableSearch] = useState('')
  const [copyStatus, setCopyStatus] = useState('')

  useEffect(() => {
    const initialize = async () => {
      try {
        const metadata = await loadStatisticsMetadata()
        const availableKeywords = metadata.keywords.map((item) => item.name)
        const availableSet = new Set(availableKeywords.map((keyword) => keyword.toLocaleLowerCase('ko-KR')))
        const stored = parseKeywords(loadSavedKeywords().join('\n'))
        const initialKeywords = stored.some((keyword) => availableSet.has(keyword.toLocaleLowerCase('ko-KR')))
          ? stored
          : availableKeywords.slice(0, 1)
        const canonicalMap = new Map(
          availableKeywords.map((keyword) => [keyword.toLocaleLowerCase('ko-KR'), keyword]),
        )
        const matched = initialKeywords
          .map((keyword) => canonicalMap.get(keyword.toLocaleLowerCase('ko-KR')))
          .filter((keyword): keyword is string => Boolean(keyword))
        const initialFilters: StatisticFilters = {
          startMonth: metadata.minMonth,
          endMonth: metadata.maxMonth,
          keywords: matched,
          ageGroups: [...AGE_GROUPS],
          devices: [...DEVICES],
          genders: [...GENDERS],
        }

        if (!metadata.minMonth || !metadata.maxMonth || !matched.length) {
          setPayload({ generatedAt: metadata.generatedAt, source: metadata.source, facts: [] })
          setKeywords(availableKeywords)
          setSavedKeywords(initialKeywords)
          setManagerText(initialKeywords.join('\n'))
          setKeywordInput(initialKeywords.join('\n'))
          setDraftFilters(initialFilters)
          setAppliedFilters(initialFilters)
          return
        }

        const data = await loadStatistics(initialFilters)
        setPayload(data)
        setKeywords(availableKeywords)
        setSavedKeywords(initialKeywords)
        setManagerText(initialKeywords.join('\n'))
        setKeywordInput(initialKeywords.join('\n'))
        setDraftFilters(initialFilters)
        setAppliedFilters(initialFilters)
      } catch (error: unknown) {
        setLoadError(error instanceof Error ? error.message : '데이터를 불러오지 못했습니다.')
      }
    }

    void initialize()
  }, [])

  const availableKeywordMap = useMemo(
    () => new Map(keywords.map((keyword) => [keyword.toLocaleLowerCase('ko-KR'), keyword])),
    [keywords],
  )

  const enteredKeywordSet = useMemo(
    () => new Set(parseKeywords(keywordInput).map((keyword) => keyword.toLocaleLowerCase('ko-KR'))),
    [keywordInput],
  )

  const filteredRecords = useMemo(
    () => applyFilters(payload?.facts ?? [], appliedFilters),
    [payload, appliedFilters],
  )

  const result = useMemo(
    () => aggregateStatistics(filteredRecords, xAxis, seriesDimension, separator),
    [filteredRecords, xAxis, seriesDimension, separator],
  )

  const visibleRows = useMemo(() => {
    const search = tableSearch.trim().toLocaleLowerCase('ko-KR')
    if (!search) return result.rows
    return result.rows.filter((row) =>
      [row.panel, row.xAxis, row.series, row.impression]
        .join(' ')
        .toLocaleLowerCase('ko-KR')
        .includes(search),
    )
  }, [result.rows, tableSearch])

  const sharedMaximum = useMemo(
    () => Math.max(1, ...result.panels.flatMap((panel) => panel.series.flatMap((series) => series.values))),
    [result.panels],
  )

  const totalImpressions = filteredRecords.reduce((sum, record) => sum + record.impression, 0)

  const applySearch = async () => {
    if (!draftFilters.startMonth || !draftFilters.endMonth) {
      setValidationMessage('조회 시작월과 종료월을 선택해 주세요.')
      return
    }
    if (draftFilters.startMonth > draftFilters.endMonth) {
      setValidationMessage('시작월은 종료월보다 늦을 수 없습니다.')
      return
    }

    const entered = parseKeywords(keywordInput)
    if (entered.length === 0) {
      setValidationMessage('조회할 키워드를 한 줄에 하나씩 입력해 주세요.')
      return
    }

    const matched: string[] = []
    const missing: string[] = []
    for (const keyword of entered) {
      const canonical = availableKeywordMap.get(keyword.toLocaleLowerCase('ko-KR'))
      if (canonical) matched.push(canonical)
      else missing.push(keyword)
    }

    setMissingKeywords(missing)
    setValidationMessage(
      matched.length === 0 ? '입력한 키워드에 해당하는 통계 데이터가 없습니다.' : '',
    )
    const nextFilters = { ...draftFilters, keywords: matched }
    setDraftFilters(nextFilters)
    if (!matched.length) {
      setAppliedFilters(nextFilters)
      setPayload((current) => current ? { ...current, facts: [] } : current)
      return
    }

    setQuerying(true)
    try {
      const data = await loadStatistics(nextFilters)
      setPayload(data)
      setAppliedFilters(nextFilters)
    } catch (error) {
      setValidationMessage(error instanceof Error ? error.message : '통계 데이터를 조회하지 못했습니다.')
    } finally {
      setQuerying(false)
    }
  }

  const selectXAxis = (next: Dimension) => {
    setXAxis(next)
    if (next === seriesDimension) {
      setSeriesDimension(DIMENSIONS.find((item) => item.value !== next)!.value)
    }
  }

  const selectSeries = (next: Dimension) => {
    setSeriesDimension(next)
    if (next === xAxis) {
      setXAxis(DIMENSIONS.find((item) => item.value !== next)!.value)
    }
  }

  const toggleKeyword = (keyword: string) => {
    const current = parseKeywords(keywordInput)
    const normalized = keyword.toLocaleLowerCase('ko-KR')
    const next = enteredKeywordSet.has(normalized)
      ? current.filter((item) => item.toLocaleLowerCase('ko-KR') !== normalized)
      : [...current, keyword]
    setKeywordInput(next.slice(0, 100).join('\n'))
  }

  const openKeywordManager = () => {
    setManagerText(savedKeywords.join('\n'))
    setManagerOpen(true)
  }

  const saveKeywordManager = () => {
    const next = parseKeywords(managerText)
    localStorage.setItem(KEYWORD_STORAGE_KEY, JSON.stringify(next))
    setSavedKeywords(next)
    setKeywordInput(next.join('\n'))
    setManagerOpen(false)
  }

  const tableMatrix = (rows: TableRow[]) => {
    const header = separator === 'none'
      ? [dimensionLabel(xAxis), dimensionLabel(seriesDimension), '노출수']
      : ['세분화', dimensionLabel(xAxis), dimensionLabel(seriesDimension), '노출수']
    const body = rows.map((row) =>
      separator === 'none'
        ? [row.xAxis, row.series, row.impression]
        : [row.panel, row.xAxis, row.series, row.impression],
    )
    return [header, ...body]
  }

  const copyTable = async () => {
    await navigator.clipboard.writeText(tableMatrix(visibleRows).map((row) => row.join('\t')).join('\n'))
    setCopyStatus('복사됨')
    window.setTimeout(() => setCopyStatus(''), 1600)
  }

  const downloadCsv = () => {
    const csv = tableMatrix(visibleRows)
      .map((row) => row.map(escapeCsv).join(','))
      .join('\r\n')
    const url = URL.createObjectURL(new Blob([`\uFEFF${csv}`], { type: 'text/csv;charset=utf-8' }))
    const anchor = document.createElement('a')
    anchor.href = url
    anchor.download = `keyword-statistics-${appliedFilters.startMonth}-${appliedFilters.endMonth}.csv`
    anchor.click()
    URL.revokeObjectURL(url)
  }

  if (loadError) return <div className="page-message error">{loadError}</div>
  if (!payload) return <div className="page-message">통계 데이터를 불러오는 중입니다…</div>

  return (
    <section className="keyword-page">
      <div className="tool-controls">
        <div className="tool-control-inner section-container">
          <div className="date-fields">
            <label>
              시작월
              <input
                type="month"
                value={draftFilters.startMonth}
                onChange={(event) => setDraftFilters({ ...draftFilters, startMonth: event.target.value })}
              />
            </label>
            <span aria-hidden="true">–</span>
            <label>
              종료월
              <input
                type="month"
                value={draftFilters.endMonth}
                onChange={(event) => setDraftFilters({ ...draftFilters, endMonth: event.target.value })}
              />
            </label>
          </div>

          <div className="keyword-entry">
            <div className="keyword-entry-heading">
              <label htmlFor="keyword-input">키워드 입력 <span>{parseKeywords(keywordInput).length}/100</span></label>
            </div>
            <textarea
              id="keyword-input"
              rows={3}
              value={keywordInput}
              placeholder={'조회할 키워드를 한 줄에 하나씩 입력하세요.\n예: 룩옵티컬'}
              onChange={(event) => setKeywordInput(event.target.value)}
            />
            <div className="keyword-entry-footer">
              {savedKeywords.length > 0 ? (
                <div className="keyword-quick-list" aria-label="저장 키워드 빠른 선택">
                  {savedKeywords.map((keyword) => (
                    <button
                      type="button"
                      key={keyword}
                      className={enteredKeywordSet.has(keyword.toLocaleLowerCase('ko-KR')) ? 'active' : ''}
                      onClick={() => toggleKeyword(keyword)}
                    >
                      {keyword}
                    </button>
                  ))}
                </div>
              ) : <span />}
              <div className="keyword-entry-actions">
                <button className="button button-secondary keyword-manager-button" type="button" onClick={openKeywordManager}>키워드 관리</button>
                <button className="button button-primary search-button" type="button" disabled={querying} onClick={() => void applySearch()}>
                  {querying ? '조회 중' : '조회'}
                </button>
              </div>
            </div>
          </div>
        </div>
        {(validationMessage || missingKeywords.length > 0) && (
          <div className="query-feedback section-container">
            {validationMessage && <p className="control-message">{validationMessage}</p>}
            {missingKeywords.length > 0 && (
              <p>통계 없음: {missingKeywords.join(', ')}</p>
            )}
          </div>
        )}
      </div>

      <div className="tool-body section-container">
        <aside className="filter-panel">
          <div className="filter-panel-heading">
            <span className="eyebrow">VIEW OPTIONS</span>
            <h1>표시 기준</h1>
          </div>

          <SegmentedControl
            label="X축"
            value={xAxis}
            options={DIMENSIONS.map((item) => ({ ...item, disabled: item.value === seriesDimension }))}
            onChange={selectXAxis}
          />
          <SegmentedControl
            label="시리즈"
            value={seriesDimension}
            options={DIMENSIONS.map((item) => ({ ...item, disabled: item.value === xAxis }))}
            onChange={selectSeries}
          />
          <SegmentedControl
            label="세분화"
            value={separator}
            options={[
              { value: 'none', label: '없음' },
              ...DIMENSIONS.map((item) => ({ value: item.value, label: item.label })),
            ]}
            onChange={setSeparator}
          />

          <ToggleChips
            label="성별 필터"
            values={GENDERS}
            selected={draftFilters.genders}
            labelFor={(value) => valueLabel('gender', value)}
            onChange={(values) => setDraftFilters({ ...draftFilters, genders: values })}
          />
          <ToggleChips
            label="디바이스 필터"
            values={DEVICES}
            selected={draftFilters.devices}
            labelFor={(value) => valueLabel('device', value)}
            onChange={(values) => setDraftFilters({ ...draftFilters, devices: values })}
          />
          <ToggleChips
            label="연령대 필터"
            values={AGE_GROUPS}
            selected={draftFilters.ageGroups}
            labelFor={(value) => valueLabel('ageGroup', value)}
            onChange={(values) => setDraftFilters({ ...draftFilters, ageGroups: values })}
          />

          <div className="layout-options">
            <span>한 줄 차트 수</span>
            <div className="compact-segments">
              {[1, 2, 3, 4].map((value) => (
                <button type="button" key={value} className={columns === value ? 'active' : ''} onClick={() => setColumns(value)}>
                  {value}
                </button>
              ))}
            </div>
          </div>
          <label className="switch-row">
            <input type="checkbox" checked={equalYAxis} onChange={(event) => setEqualYAxis(event.target.checked)} />
            <span>차트 Y축 범위 통일</span>
          </label>
          <p className="filter-hint">필터 변경 후 상단의 조회 버튼을 누르면 결과에 반영됩니다.</p>
        </aside>

        <div className="results-panel">
          <div className="result-summary">
            <div><span>총 노출수</span><strong>{totalImpressions.toLocaleString('ko-KR')}</strong></div>
            <div><span>원본 행</span><strong>{filteredRecords.length.toLocaleString('ko-KR')}</strong></div>
            <div><span>표시 기준</span><strong>{dimensionLabel(xAxis)} × {dimensionLabel(seriesDimension)}</strong></div>
          </div>

          {result.panels.length === 0 ? (
            <div className="empty-state">
              <strong>조회된 통계가 없습니다.</strong>
              <span>키워드, 기간 또는 필터 조건을 확인해 주세요.</span>
            </div>
          ) : (
            <>
              <div className="chart-grid" style={{ '--chart-columns': columns } as React.CSSProperties}>
                {result.panels.map((panel) => (
                  <article className="chart-card" key={panel.key}>
                    <header><div><h2>{panel.title}</h2><span>{panel.total.toLocaleString('ko-KR')}회</span></div></header>
                    {xAxis === 'month' ? (
                      <LineChart panel={panel} sharedMaximum={equalYAxis ? sharedMaximum : undefined} />
                    ) : (
                      <BarChart panel={panel} sharedMaximum={equalYAxis ? sharedMaximum : undefined} />
                    )}
                  </article>
                ))}
              </div>

              <section className="data-section">
                <div className="data-section-heading">
                  <div><span className="eyebrow">DETAIL</span><h2>집계 데이터</h2></div>
                  <div className="table-actions">
                    <input
                      type="search"
                      value={tableSearch}
                      placeholder="결과 검색"
                      aria-label="집계 결과 검색"
                      onChange={(event) => setTableSearch(event.target.value)}
                    />
                    <button type="button" onClick={copyTable}>{copyStatus || '복사'}</button>
                    <button type="button" onClick={downloadCsv}>CSV</button>
                    <span>{visibleRows.length.toLocaleString('ko-KR')}개</span>
                  </div>
                </div>
                <div className="table-scroll">
                  <table>
                    <thead><tr>
                      {separator !== 'none' && <th>세분화</th>}
                      <th>{dimensionLabel(xAxis)}</th>
                      <th>{dimensionLabel(seriesDimension)}</th>
                      <th className="numeric">노출수</th>
                    </tr></thead>
                    <tbody>
                      {visibleRows.map((row, index) => (
                        <tr key={`${row.panel}-${row.xAxis}-${row.series}-${index}`}>
                          {separator !== 'none' && <td>{row.panel}</td>}
                          <td>{row.xAxis}</td>
                          <td>{row.series}</td>
                          <td className="numeric">{row.impression.toLocaleString('ko-KR')}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </section>
            </>
          )}
        </div>
      </div>

      {managerOpen && (
        <div className="modal-backdrop" role="presentation" onMouseDown={() => setManagerOpen(false)}>
          <section
            className="keyword-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="keyword-manager-title"
            onMouseDown={(event) => event.stopPropagation()}
          >
            <header>
              <div><span className="eyebrow">KEYWORD MANAGER</span><h2 id="keyword-manager-title">키워드 추가 및 관리</h2></div>
              <button type="button" className="modal-close" aria-label="닫기" onClick={() => setManagerOpen(false)}>×</button>
            </header>

            <div className="collection-status">
              <i />
              <div>
                <strong>{payload.source === 'd1' ? 'D1 통계 데이터가 연결되어 있습니다.' : '정적 통계 데이터가 준비되어 있습니다.'}</strong>
                <span>
                  {payload.generatedAt
                    ? `데이터 기준: ${new Date(payload.generatedAt).toLocaleString('ko-KR')}`
                    : '데이터 기준 정보가 없습니다.'}
                </span>
              </div>
            </div>

            <div className="keyword-manager-grid">
              <label>
                저장 키워드 <span>({parseKeywords(managerText).length}/100)</span>
                <textarea
                  rows={12}
                  value={managerText}
                  placeholder="한 줄에 하나씩 입력하세요."
                  onChange={(event) => setManagerText(event.target.value)}
                />
              </label>
              <div className="available-keywords">
                <strong>조회 가능한 키워드 ({keywords.length})</strong>
                <div>
                  {keywords.map((keyword) => (
                    <button
                      type="button"
                      key={keyword}
                      onClick={() => {
                        const current = parseKeywords(managerText)
                        if (!current.some((item) => item.toLocaleLowerCase('ko-KR') === keyword.toLocaleLowerCase('ko-KR'))) {
                          setManagerText([...current, keyword].slice(0, 100).join('\n'))
                        }
                      }}
                    >
                      <span>{keyword}</span><em>수집 완료</em>
                    </button>
                  ))}
                </div>
                <p>목록에 없는 키워드도 저장할 수 있지만 통계 JSON에 데이터가 있어야 조회됩니다.</p>
              </div>
            </div>

            <footer>
              <button type="button" className="button button-ghost" onClick={() => setManagerOpen(false)}>취소</button>
              <button type="button" className="button button-primary" onClick={saveKeywordManager}>저장</button>
            </footer>
          </section>
        </div>
      )}
    </section>
  )
}
